import { Matrix4, Quaternion, Vector3 } from 'three'
import { resolveUnitSeatHeightCm } from '@utils/atemporal/seatHeightCatalog'
import { targetSeatCushionLocalY } from '@utils/atemporal/seatHeightPlacement'

const MAX_LOCAL_AXIS_MM = 600

function resolveParentSeatHeightCm(parentItem?: any): number {
  return resolveUnitSeatHeightCm(parentItem)
}

function seatHeightLocalY(parentItem?: any): number {
  return targetSeatCushionLocalY(resolveParentSeatHeightCm(parentItem))
}

export function isAtemporalSeatOrCushionLinkedSon(item: any): boolean {
  if (!item || item.type !== 'linkedSon') return false
  const catalog = String(item.catalogID || item.referencia || '').toLowerCase()
  return catalog.includes('asiento') || catalog.includes('cojin')
}

export type SeatCushionLocalCoords = {
  x: number
  y: number
  z: number
  matrix: Matrix4
}

/** Calcula offset local respecto al padre (sin mutar el item). */
export function resolveSeatCushionLocalCoords(
  item: any,
  parentItem?: any,
): SeatCushionLocalCoords | null {
  if (!isAtemporalSeatOrCushionLinkedSon(item)) return null

  const catalog = String(item.catalogID || item.referencia || '').toLowerCase()
  const isSeat = catalog.includes('asiento')
  const isCushion = catalog.includes('cojin')

  let localX = Number(item.x)
  let localY = Number(item.y)
  let localZ = Number(item.z)

  if (parentItem?.matrix) {
    const parentPos = new Vector3()
    parentItem.matrix.decompose(parentPos, new Quaternion(), new Vector3())
    const nearParentWorld =
      Math.abs(localX - parentPos.x) < 250 &&
      Math.abs(localY - parentPos.y) < 250 &&
      Math.abs(localZ - parentPos.z) < 250
    const looksLikeWorldSceneCoords =
      Math.abs(localX) > 800 || Math.abs(localZ) > 800 || Math.abs(localY) > 1200

    if (nearParentWorld || looksLikeWorldSceneCoords) {
      localX = 0
      localY = 0
      localZ = 0
    }
  }

  const heightOffsetY = seatHeightLocalY(parentItem)

  if (isSeat) {
    localX = 0
    localY = heightOffsetY
    localZ = 0
  } else if (isCushion) {
    localX = 0
    localZ = 0
    localY = heightOffsetY
  }

  return {
    x: localX,
    y: localY,
    z: localZ,
    matrix: new Matrix4().setPosition(localX, localY, localZ),
  }
}

/** Solo usar dentro de `set()` de Zustand (draft Immer). */
export function enforceSeatCushionLocalCoords(item: any, parentItem?: any): void {
  const resolved = resolveSeatCushionLocalCoords(item, parentItem)
  if (!resolved) return
  item.x = resolved.x
  item.y = resolved.y
  item.z = resolved.z
  item.matrix = resolved.matrix
}

/** Hijos linkedSon: si el padre está en la multiselección, se mueven con él (no como pivotes independientes). */
export function shouldSkipGroupedDragForLinkedSon(item: any, selectedItems: Set<unknown>): boolean {
  if (!item || item.type !== 'linkedSon') return false
  const parentId = String(item.firstParentID || '')
  return Boolean(parentId && selectedItems.has(parentId))
}
